import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog,
  DialogActions, DialogContent, DialogTitle, FormControl, InputLabel,
  IconButton, InputAdornment, MenuItem, Paper, Select, Switch, Tab, Tabs,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField,
  Tooltip, Typography
} from '@mui/material';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { toast } from 'react-toastify';
import { api, useAuth } from '../services/AuthService';

const number = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
};

const units = [
  ['un', 'Unidade'], ['kg', 'Quilograma'], ['g', 'Grama'],
  ['l', 'Litro'], ['ml', 'Mililitro']
];

const movementLabels = {
  ENTRY: 'Entrada',
  SALE: 'Venda',
  ADJUSTMENT: 'Ajuste',
  LOSS: 'Perda'
};

function Estoque() {
  const { isAdminMaster, getLanchoneteId } = useAuth();
  const [tab, setTab] = useState(0);
  const [produtos, setProdutos] = useState([]);
  const [insumos, setInsumos] = useState([]);
  const [movements, setMovements] = useState([]);
  const [expiry, setExpiry] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [ingredientDialog, setIngredientDialog] = useState(false);
  const [editingIngredient, setEditingIngredient] = useState(null);
  const [movementDialog, setMovementDialog] = useState(null);
  const [recipeProductId, setRecipeProductId] = useState('');
  const [copyRecipeProductId, setCopyRecipeProductId] = useState('');
  const [recipeDraft, setRecipeDraft] = useState({});
  const [productSearch, setProductSearch] = useState('');
  const [ingredientSearch, setIngredientSearch] = useState('');
  const [recipeSearch, setRecipeSearch] = useState('');
  const [movementSearch, setMovementSearch] = useState('');
  const [movementTypeFilter, setMovementTypeFilter] = useState('ALL');
  const [ingredientForm, setIngredientForm] = useState({
    name: '', unit: 'un', minimum_quantity: 0, track_expiry: false
  });
  const [movementForm, setMovementForm] = useState({
    movement_type: 'ENTRY', quantity: 1, reason: '', expiry_date: '',
    unit_cost: '', adjustment_direction: 1, use_packages: false,
    package_count: 1, units_per_package: 1
  });

  const lanchoneteId = getLanchoneteId();
  const storeQuery = isAdminMaster() && lanchoneteId ? `?lanchonete_id=${lanchoneteId}` : '';

  const loadAll = useCallback(async () => {
    try {
      setLoading(true);
      const productUrl = isAdminMaster() || !lanchoneteId
        ? '/produtos'
        : `/produtos?lanchonete_id=${lanchoneteId}`;
      const [productsResponse, itemsResponse, movementsResponse, expiryResponse] = await Promise.all([
        api.get(productUrl),
        api.get(`/inventory/items${storeQuery}`),
        api.get(`/inventory/movements${storeQuery}`),
        api.get(`/inventory/expiry${storeQuery}`)
      ]);
      const productItems = Array.isArray(productsResponse.data) ? productsResponse.data : [];
      setProdutos(productItems);
      setInsumos(Array.isArray(itemsResponse.data) ? itemsResponse.data : []);
      setMovements(Array.isArray(movementsResponse.data) ? movementsResponse.data : []);
      setExpiry(Array.isArray(expiryResponse.data) ? expiryResponse.data : []);
      setDrafts(Object.fromEntries(productItems.map((produto) => [
        produto.id,
        { stock_enabled: Boolean(produto.stock_enabled), stock_quantity: number(produto.stock_quantity) }
      ])));
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao carregar estoque');
    } finally {
      setLoading(false);
    }
  }, [isAdminMaster, lanchoneteId, storeQuery]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const lowStock = useMemo(() => insumos.filter((item) => item.low_stock), [insumos]);
  const expired = useMemo(() => expiry.filter((batch) => batch.expired), [expiry]);
  const expiringSoon = useMemo(() => expiry.filter((batch) => !batch.expired && batch.expiring_soon), [expiry]);
  const visibleProducts = useMemo(() => {
    const term = productSearch.trim().toLowerCase();
    return term ? produtos.filter((item) => `${item.name} ${item.category || ''}`.toLowerCase().includes(term)) : produtos;
  }, [produtos, productSearch]);
  const visibleIngredients = useMemo(() => {
    const term = ingredientSearch.trim().toLowerCase();
    return term ? insumos.filter((item) => item.name.toLowerCase().includes(term)) : insumos;
  }, [insumos, ingredientSearch]);
  const visibleRecipeIngredients = useMemo(() => {
    const term = recipeSearch.trim().toLowerCase();
    return term ? insumos.filter((item) => item.name.toLowerCase().includes(term)) : insumos;
  }, [insumos, recipeSearch]);
  const visibleMovements = useMemo(() => {
    const term = movementSearch.trim().toLowerCase();
    return movements.filter((item) => {
      if (movementTypeFilter !== 'ALL' && item.movement_type !== movementTypeFilter) return false;
      return !term || `${item.item_name} ${item.reason || ''}`.toLowerCase().includes(term);
    });
  }, [movements, movementSearch, movementTypeFilter]);

  const updateDraft = (id, changes) => {
    setDrafts((current) => ({ ...current, [id]: { ...current[id], ...changes } }));
  };

  const saveProductStock = async (produto) => {
    const draft = drafts[produto.id];
    try {
      setSavingId(produto.id);
      await api.patch(`/produtos/${produto.id}/stock`, {
        stock_enabled: Boolean(draft.stock_enabled),
        stock_quantity: Math.floor(number(draft.stock_quantity))
      });
      toast.success(`Estoque de ${produto.name} atualizado`);
      await loadAll();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao atualizar produto');
    } finally {
      setSavingId(null);
    }
  };

  const openIngredientForm = (item = null) => {
    setEditingIngredient(item);
    setIngredientForm(item ? {
      name: item.name,
      unit: item.unit,
      minimum_quantity: number(item.minimum_quantity),
      track_expiry: Boolean(item.track_expiry)
    } : { name: '', unit: 'un', minimum_quantity: 0, track_expiry: false });
    setIngredientDialog(true);
  };

  const saveIngredient = async () => {
    try {
      const payload = {
        ...ingredientForm,
        lanchonete_id: lanchoneteId,
        minimum_quantity: number(ingredientForm.minimum_quantity)
      };
      if (editingIngredient) {
        await api.put(`/inventory/items/${editingIngredient.id}${storeQuery}`, payload);
      } else {
        await api.post(`/inventory/items${storeQuery}`, payload);
      }
      setIngredientDialog(false);
      setEditingIngredient(null);
      setIngredientForm({ name: '', unit: 'un', minimum_quantity: 0, track_expiry: false });
      toast.success(editingIngredient ? 'Insumo atualizado' : 'Insumo cadastrado');
      await loadAll();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao cadastrar insumo');
    }
  };

  const openMovement = (item, type) => {
    setMovementDialog(item);
    setMovementForm({
      movement_type: type,
      quantity: 1,
      reason: type === 'ENTRY' ? 'Compra de estoque' : type === 'LOSS' ? 'Perda de estoque' : 'Ajuste manual',
      expiry_date: '',
      unit_cost: '',
      adjustment_direction: 1,
      use_packages: false,
      package_count: 1,
      units_per_package: 1
    });
  };

  const openBatchLoss = (batch) => {
    const item = insumos.find((ingredient) => Number(ingredient.id) === Number(batch.inventory_item_id));
    if (!item) return;
    setMovementDialog(item);
    setMovementForm({
      movement_type: 'LOSS',
      quantity: number(batch.quantity_remaining),
      reason: `Perda por validade do lote ${batch.id}`,
      expiry_date: '',
      unit_cost: '',
      adjustment_direction: -1,
      use_packages: false,
      package_count: 1,
      units_per_package: 1
    });
  };

  const saveMovement = async () => {
    try {
      const movementQuantity = movementForm.movement_type === 'ENTRY' && movementForm.use_packages
        ? number(movementForm.package_count) * number(movementForm.units_per_package)
        : number(movementForm.quantity);
      await api.post(`/inventory/items/${movementDialog.id}/movements${storeQuery}`, {
        ...movementForm,
        lanchonete_id: lanchoneteId,
        quantity: movementQuantity,
        unit_cost: movementForm.unit_cost === '' ? null : number(movementForm.unit_cost),
        expiry_date: movementForm.expiry_date || null
      });
      setMovementDialog(null);
      toast.success('Movimentação registrada');
      await loadAll();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao movimentar estoque');
    }
  };

  const loadRecipe = async (productId) => {
    setRecipeProductId(productId);
    if (!productId) return setRecipeDraft({});
    try {
      const response = await api.get(`/inventory/recipes/${productId}${storeQuery}`);
      setRecipeDraft(Object.fromEntries(response.data.map((item) => [
        Number(item.inventory_item_id), number(item.quantity)
      ])));
    } catch (error) {
      toast.error('Erro ao carregar ficha técnica');
    }
  };

  const saveRecipe = async () => {
    const ingredients = Object.entries(recipeDraft)
      .filter(([, quantity]) => number(quantity) > 0)
      .map(([inventory_item_id, quantity]) => ({
        inventory_item_id: Number(inventory_item_id),
        quantity: number(quantity)
      }));
    try {
      await api.put(`/inventory/recipes/${recipeProductId}${storeQuery}`, {
        lanchonete_id: lanchoneteId,
        ingredients
      });
      toast.success('Ficha técnica salva');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao salvar ficha técnica');
    }
  };

  const copyRecipe = async () => {
    if (!copyRecipeProductId) return;
    try {
      const response = await api.get(`/inventory/recipes/${copyRecipeProductId}${storeQuery}`);
      setRecipeDraft(Object.fromEntries(response.data.map((item) => [
        Number(item.inventory_item_id), number(item.quantity)
      ])));
      toast.success('Ficha copiada. Confira e clique em Salvar ficha técnica.');
    } catch (error) {
      toast.error('Erro ao copiar ficha técnica');
    }
  };

  if (loading) return <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>;

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1} mb={1}>
        <Inventory2Icon color="primary" />
        <Typography variant="h4" fontWeight="bold">Estoque avançado</Typography>
      </Box>
      <Typography color="text.secondary" mb={2}>
        Controle produtos prontos, insumos, fichas técnicas, perdas e validade.
      </Typography>

      <Box display="grid" gridTemplateColumns={{ xs: '1fr 1fr', md: 'repeat(4, 1fr)' }} gap={2} mb={3}>
        <Card><CardContent><Typography color="text.secondary">Insumos</Typography><Typography variant="h4">{insumos.length}</Typography></CardContent></Card>
        <Card><CardContent><Typography color="text.secondary">Estoque baixo</Typography><Typography variant="h4" color="warning.main">{lowStock.length}</Typography></CardContent></Card>
        <Card><CardContent><Typography color="text.secondary">Vencendo em 30 dias</Typography><Typography variant="h4" color="warning.main">{expiringSoon.length}</Typography></CardContent></Card>
        <Card><CardContent><Typography color="text.secondary">Vencidos</Typography><Typography variant="h4" color="error">{expired.length}</Typography></CardContent></Card>
      </Box>

      {lowStock.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {lowStock.length} insumo(s) estão no estoque mínimo ou abaixo dele.
        </Alert>
      )}

      <Paper sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(_, value) => setTab(value)} variant="scrollable">
          <Tab label="Produtos prontos" />
          <Tab label="Insumos" />
          <Tab label="Fichas técnicas" />
          <Tab label="Entradas e saídas" />
          <Tab label="Perdas e validade" />
        </Tabs>
      </Paper>

      {tab === 0 && (
        <>
          <Alert severity="info" sx={{ mb: 2 }}>
            Use este controle para bebidas e produtos prontos. Produtos sem controle continuam ilimitados.
          </Alert>
          <TextField
            fullWidth
            sx={{ mb: 2 }}
            placeholder="Buscar produto ou categoria"
            value={productSearch}
            onChange={(e) => setProductSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
          />
          <TableContainer component={Paper}>
            <Table>
              <TableHead><TableRow><TableCell>Produto</TableCell><TableCell>Categoria</TableCell><TableCell>Controlar</TableCell><TableCell>Quantidade</TableCell><TableCell>Situação</TableCell><TableCell /></TableRow></TableHead>
              <TableBody>{visibleProducts.map((produto) => {
                const draft = drafts[produto.id] || { stock_enabled: false, stock_quantity: 0 };
                const out = draft.stock_enabled && number(draft.stock_quantity) === 0;
                return <TableRow key={produto.id}>
                  <TableCell><b>{produto.name}</b></TableCell>
                  <TableCell>{produto.category || '-'}</TableCell>
                  <TableCell><Switch checked={draft.stock_enabled} onChange={(e) => updateDraft(produto.id, { stock_enabled: e.target.checked })} /></TableCell>
                  <TableCell><TextField size="small" type="number" disabled={!draft.stock_enabled} value={draft.stock_quantity} onChange={(e) => updateDraft(produto.id, { stock_quantity: e.target.value })} inputProps={{ min: 0, step: 1 }} sx={{ width: 120 }} /></TableCell>
                  <TableCell><Chip size="small" color={out ? 'error' : draft.stock_enabled ? 'success' : 'default'} label={out ? 'Esgotado' : draft.stock_enabled ? 'Disponível' : 'Ilimitado'} /></TableCell>
                  <TableCell><Button variant="contained" size="small" startIcon={<SaveIcon />} disabled={savingId === produto.id} onClick={() => saveProductStock(produto)}>Salvar</Button></TableCell>
                </TableRow>;
              })}</TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {tab === 1 && (
        <>
          <Alert severity="info" sx={{ mb: 2 }}>
            Cadastre ingredientes e materiais. Depois use Entrada para informar compras e Ficha técnica para ligar os insumos aos produtos.
          </Alert>
          <Box display="flex" gap={2} justifyContent="space-between" mb={2} flexWrap="wrap">
            <TextField
              placeholder="Buscar insumo"
              value={ingredientSearch}
              onChange={(e) => setIngredientSearch(e.target.value)}
              sx={{ minWidth: 280, flex: 1 }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
            />
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => openIngredientForm()}>Novo insumo</Button>
          </Box>
          <TableContainer component={Paper}>
            <Table>
              <TableHead><TableRow><TableCell>Insumo</TableCell><TableCell>Saldo</TableCell><TableCell>Mínimo</TableCell><TableCell>Validade</TableCell><TableCell>Situação</TableCell><TableCell>Ações</TableCell></TableRow></TableHead>
              <TableBody>{visibleIngredients.map((item) => <TableRow key={item.id}>
                <TableCell><b>{item.name}</b></TableCell>
                <TableCell>{number(item.current_quantity)} {item.unit}</TableCell>
                <TableCell>{number(item.minimum_quantity)} {item.unit}</TableCell>
                <TableCell>{item.track_expiry ? 'Controlada' : 'Não controlada'}</TableCell>
                <TableCell><Chip size="small" color={item.low_stock ? 'warning' : 'success'} label={item.low_stock ? 'Estoque baixo' : 'Normal'} /></TableCell>
                <TableCell>
                  <Box display="flex" gap={1} flexWrap="wrap">
                    <Button size="small" variant="contained" onClick={() => openMovement(item, 'ENTRY')}>Entrada</Button>
                    <Button size="small" variant="outlined" onClick={() => openMovement(item, 'ADJUSTMENT')}>Ajuste</Button>
                    <Button size="small" color="error" variant="outlined" onClick={() => openMovement(item, 'LOSS')}>Perda</Button>
                    <Tooltip title="Editar nome, unidade, estoque mínimo e validade">
                      <IconButton size="small" onClick={() => openIngredientForm(item)}><EditIcon fontSize="small" /></IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>)}</TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {tab === 2 && (
        <Paper sx={{ p: 3 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            Exemplo: se 1 X-Burger usa 1 pão e 2 fatias de queijo, informe 1 no pão e 2 no queijo. A baixa será automática a cada venda.
          </Alert>
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>Produto</InputLabel>
            <Select value={recipeProductId} label="Produto" onChange={(e) => loadRecipe(e.target.value)}>
              {produtos.map((produto) => <MenuItem key={produto.id} value={produto.id}>{produto.name}</MenuItem>)}
            </Select>
          </FormControl>
          {!recipeProductId ? <Alert severity="info">Selecione um produto para montar a ficha técnica.</Alert> : (
            <>
              <Typography fontWeight="bold" mb={1}>Consumo para produzir 1 unidade</Typography>
              {insumos.length === 0 && <Alert severity="warning">Cadastre os insumos primeiro.</Alert>}
              <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr auto' }} gap={2} mb={2}>
                <TextField
                  placeholder="Buscar insumo na ficha"
                  value={recipeSearch}
                  onChange={(e) => setRecipeSearch(e.target.value)}
                  InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
                />
                <FormControl>
                  <InputLabel>Copiar ficha de outro produto</InputLabel>
                  <Select value={copyRecipeProductId} label="Copiar ficha de outro produto" onChange={(e) => setCopyRecipeProductId(e.target.value)}>
                    {produtos.filter((produto) => Number(produto.id) !== Number(recipeProductId)).map((produto) => <MenuItem key={produto.id} value={produto.id}>{produto.name}</MenuItem>)}
                  </Select>
                </FormControl>
                <Button variant="outlined" startIcon={<ContentCopyIcon />} disabled={!copyRecipeProductId} onClick={copyRecipe}>Copiar</Button>
              </Box>
              <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: 'repeat(2, 1fr)' }} gap={2}>
                {visibleRecipeIngredients.map((item) => <TextField key={item.id} type="number" label={`${item.name} (${item.unit})`} value={recipeDraft[item.id] || ''} onChange={(e) => setRecipeDraft((current) => ({ ...current, [item.id]: e.target.value }))} inputProps={{ min: 0, step: 0.001 }} helperText="Deixe vazio se não fizer parte da receita" />)}
              </Box>
              <Button sx={{ mt: 3 }} variant="contained" startIcon={<SaveIcon />} onClick={saveRecipe}>Salvar ficha técnica</Button>
            </>
          )}
        </Paper>
      )}

      {tab === 3 && (
        <>
        <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: '2fr 1fr' }} gap={2} mb={2}>
          <TextField
            placeholder="Buscar insumo ou motivo"
            value={movementSearch}
            onChange={(e) => setMovementSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
          />
          <FormControl><InputLabel>Tipo</InputLabel><Select value={movementTypeFilter} label="Tipo" onChange={(e) => setMovementTypeFilter(e.target.value)}><MenuItem value="ALL">Todos</MenuItem>{Object.entries(movementLabels).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}</Select></FormControl>
        </Box>
        <TableContainer component={Paper}>
          <Table>
            <TableHead><TableRow><TableCell>Data</TableCell><TableCell>Insumo</TableCell><TableCell>Tipo</TableCell><TableCell>Quantidade</TableCell><TableCell>Saldo</TableCell><TableCell>Motivo</TableCell></TableRow></TableHead>
            <TableBody>{visibleMovements.map((item) => <TableRow key={item.id}>
              <TableCell>{new Date(item.created_at).toLocaleString('pt-BR')}</TableCell>
              <TableCell>{item.item_name}</TableCell>
              <TableCell>{movementLabels[item.movement_type] || item.movement_type}</TableCell>
              <TableCell sx={{ color: number(item.quantity) === item.quantity && item.quantity >= 0 ? 'success.main' : Number(item.quantity) < 0 ? 'error.main' : 'inherit' }}>{Number(item.quantity) > 0 ? '+' : ''}{item.quantity} {item.unit}</TableCell>
              <TableCell>{item.balance_after} {item.unit}</TableCell>
              <TableCell>{item.reason || '-'}</TableCell>
            </TableRow>)}</TableBody>
          </Table>
        </TableContainer>
        </>
      )}

      {tab === 4 && (
        <>
          <Alert severity="info" sx={{ mb: 2 }}>Lotes vencidos continuam listados até que a perda seja registrada na aba Insumos.</Alert>
          <TableContainer component={Paper}>
            <Table>
              <TableHead><TableRow><TableCell>Insumo</TableCell><TableCell>Validade</TableCell><TableCell>Quantidade restante</TableCell><TableCell>Situação</TableCell><TableCell>Ação</TableCell></TableRow></TableHead>
              <TableBody>{expiry.map((batch) => <TableRow key={batch.id}>
                <TableCell>{batch.item_name}</TableCell>
                <TableCell>{new Date(`${batch.expiry_date}T12:00:00`).toLocaleDateString('pt-BR')}</TableCell>
                <TableCell>{batch.quantity_remaining} {batch.unit}</TableCell>
                <TableCell><Chip size="small" color={batch.expired ? 'error' : batch.expiring_soon ? 'warning' : 'success'} label={batch.expired ? 'Vencido' : batch.expiring_soon ? 'Vence em breve' : 'Dentro da validade'} /></TableCell>
                <TableCell>{batch.expired && <Button size="small" color="error" variant="outlined" onClick={() => openBatchLoss(batch)}>Registrar perda</Button>}</TableCell>
              </TableRow>)}</TableBody>
            </Table>
          </TableContainer>
          <Typography variant="h6" fontWeight="bold" mt={3} mb={1}>Histórico de perdas</Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead><TableRow><TableCell>Data</TableCell><TableCell>Insumo</TableCell><TableCell>Quantidade</TableCell><TableCell>Motivo</TableCell></TableRow></TableHead>
              <TableBody>{movements.filter((item) => item.movement_type === 'LOSS').map((item) => <TableRow key={item.id}>
                <TableCell>{new Date(item.created_at).toLocaleString('pt-BR')}</TableCell>
                <TableCell>{item.item_name}</TableCell>
                <TableCell>{Math.abs(Number(item.quantity))} {item.unit}</TableCell>
                <TableCell>{item.reason}</TableCell>
              </TableRow>)}</TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      <Dialog open={ingredientDialog} onClose={() => setIngredientDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editingIngredient ? 'Editar insumo' : 'Novo insumo'}</DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth margin="normal" label="Nome do insumo" value={ingredientForm.name} onChange={(e) => setIngredientForm({ ...ingredientForm, name: e.target.value })} />
          <FormControl fullWidth margin="normal"><InputLabel>Unidade</InputLabel><Select label="Unidade" value={ingredientForm.unit} onChange={(e) => setIngredientForm({ ...ingredientForm, unit: e.target.value })}>{units.map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}</Select></FormControl>
          <TextField fullWidth margin="normal" type="number" label="Avisar quando chegar em" value={ingredientForm.minimum_quantity} onChange={(e) => setIngredientForm({ ...ingredientForm, minimum_quantity: e.target.value })} inputProps={{ min: 0, step: 0.001 }} />
          <Box display="flex" alignItems="center" mt={1}><Switch checked={ingredientForm.track_expiry} onChange={(e) => setIngredientForm({ ...ingredientForm, track_expiry: e.target.checked })} /><Typography>Controlar validade dos lotes</Typography></Box>
        </DialogContent>
        <DialogActions><Button onClick={() => setIngredientDialog(false)}>Cancelar</Button><Button variant="contained" onClick={saveIngredient}>{editingIngredient ? 'Salvar alterações' : 'Cadastrar'}</Button></DialogActions>
      </Dialog>

      <Dialog open={Boolean(movementDialog)} onClose={() => setMovementDialog(null)} fullWidth maxWidth="sm">
        <DialogTitle>{movementLabels[movementForm.movement_type]} — {movementDialog?.name}</DialogTitle>
        <DialogContent>
          {movementForm.movement_type === 'ADJUSTMENT' && <FormControl fullWidth margin="normal"><InputLabel>Tipo do ajuste</InputLabel><Select label="Tipo do ajuste" value={movementForm.adjustment_direction} onChange={(e) => setMovementForm({ ...movementForm, adjustment_direction: e.target.value })}><MenuItem value={1}>Adicionar ao saldo</MenuItem><MenuItem value={-1}>Retirar do saldo</MenuItem></Select></FormControl>}
          {movementForm.movement_type === 'ENTRY' && movementDialog?.unit === 'un' && (
            <Box display="flex" alignItems="center" mt={1}><Switch checked={movementForm.use_packages} onChange={(e) => setMovementForm({ ...movementForm, use_packages: e.target.checked })} /><Typography>Informar por caixa, pacote ou fardo</Typography></Box>
          )}
          {movementForm.movement_type === 'ENTRY' && movementForm.use_packages ? (
            <>
              <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
                <TextField fullWidth margin="normal" type="number" label="Quantidade de caixas/pacotes" value={movementForm.package_count} onChange={(e) => setMovementForm({ ...movementForm, package_count: e.target.value })} inputProps={{ min: 1, step: 1 }} />
                <TextField fullWidth margin="normal" type="number" label="Unidades em cada caixa/pacote" value={movementForm.units_per_package} onChange={(e) => setMovementForm({ ...movementForm, units_per_package: e.target.value })} inputProps={{ min: 1, step: 1 }} />
              </Box>
              <Alert severity="success" sx={{ mt: 1 }}>
                Entrada total: {number(movementForm.package_count) * number(movementForm.units_per_package)} unidades
              </Alert>
            </>
          ) : (
            <TextField autoFocus fullWidth margin="normal" type="number" label={`Quantidade (${movementDialog?.unit || ''})`} value={movementForm.quantity} onChange={(e) => setMovementForm({ ...movementForm, quantity: e.target.value })} inputProps={{ min: 0.001, step: 0.001 }} />
          )}
          <TextField fullWidth margin="normal" label="Motivo" value={movementForm.reason} onChange={(e) => setMovementForm({ ...movementForm, reason: e.target.value })} />
          {movementForm.movement_type === 'ENTRY' && <>
            <TextField fullWidth margin="normal" type="date" label="Validade do lote" InputLabelProps={{ shrink: true }} value={movementForm.expiry_date} onChange={(e) => setMovementForm({ ...movementForm, expiry_date: e.target.value })} />
            <TextField fullWidth margin="normal" type="number" label="Custo por unidade (opcional)" value={movementForm.unit_cost} onChange={(e) => setMovementForm({ ...movementForm, unit_cost: e.target.value })} inputProps={{ min: 0, step: 0.01 }} />
          </>}
        </DialogContent>
        <DialogActions><Button onClick={() => setMovementDialog(null)}>Cancelar</Button><Button variant="contained" onClick={saveMovement}>Confirmar</Button></DialogActions>
      </Dialog>
    </Box>
  );
}

export default Estoque;
